import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { SimpleMarkdown } from '../components/SimpleMarkdown';
import { useAuth } from '../context/AuthContext';
import { downloadPurchaseCsv, getPurchase, regeneratePurchaseReport, type PurchaseDetail } from '../lib/api';
import { purchaseBillingLabelNl, purchaseBillingRevoked } from '../lib/purchaseBilling';

export function PurchaseDetailPage() {
  const { id } = useParams();
  const pid = Number(id);
  const { user, token, loading } = useAuth();
  const [p, setP] = useState<PurchaseDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reportBusy, setReportBusy] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const [brandingLine, setBrandingLine] = useState('');

  useEffect(() => {
    if (!token || !user || !Number.isFinite(pid)) return;
    let c = false;
    getPurchase(token, pid).then((r) => {
      if (c) return;
      if (!r.ok) setErr('Aankoop niet gevonden.');
      else setP(r.purchase);
    });
    return () => {
      c = true;
    };
  }, [token, user, pid]);

  async function saveCsv() {
    if (!token || !p) return;
    setCsvBusy(true);
    setErr(null);
    try {
      const r = await downloadPurchaseCsv(token, p.id);
      if (!r.ok) {
        setErr(r.message);
        return;
      }
      const url = URL.createObjectURL(r.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = r.filename;
      a.rel = 'noopener';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setCsvBusy(false);
    }
  }

  async function genReport() {
    if (!token || !p) return;
    setReportBusy(true);
    setErr(null);
    try {
      const r = await regeneratePurchaseReport(
        token,
        p.id,
        p.bundle_tier === 'plus' ? brandingLine : undefined
      );
      if (!r.ok) {
        setErr(r.message ?? r.error);
        return;
      }
      const refresh = await getPurchase(token, p.id);
      if (refresh.ok) setP(refresh.purchase);
    } finally {
      setReportBusy(false);
    }
  }

  if (!loading && !user) {
    return <Navigate to="/inloggen" replace />;
  }
  if (!Number.isFinite(pid)) {
    return <Navigate to="/account" replace />;
  }

  return (
    <>
      <Seo
        path={id ? `/account/aankopen/${id}` : undefined}
        title="Aankoopdetail"
        description="Je ComfortTool™-aankoop: rapport opnieuw maken en overzicht downloaden."
        keywords="ComfortTool account, aankoop, rapport"
        noindex
      />
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Link to="/account" className="text-sm font-medium text-ct-accent hover:underline">
          ← Terug naar account
        </Link>
        <h1 className="mt-4 text-2xl font-bold text-ct-text">Aankoop #{pid}</h1>
        {err && (
          <p className="mt-2 text-sm text-red-400" role="alert">
            {err}
          </p>
        )}
        {p && (
          <>
            <p className="mt-2 text-sm text-ct-muted">
              Bundel:{' '}
              <strong className="text-ct-text">
                {p.bundle_tier === 'basis' ? 'Comfort Basis' : 'Comfort Plus'}
              </strong>
              {' · '}
              Rapport gegenereerd: {p.report_regenerations_used} / {p.max_regenerations}
              {' · '}
              <span className="text-ct-text">{purchaseBillingLabelNl(p.billing_status ?? 'active')}</span>
            </p>
            {purchaseBillingRevoked(p.billing_status ?? 'active') && (
              <div
                className="mt-4 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200"
                role="status"
              >
                Deze betaling is teruggeboekt of afgesloten na een betalingsgeschil. Nieuw rapport genereren en CSV-download
                zijn uitgeschakeld. Neem bij vragen contact op met support.
              </div>
            )}
            {(p.billing_status ?? 'active') === 'dispute_open' && (
              <div
                className="mt-4 rounded-xl border border-ct-warn/40 bg-ct-warn/10 px-4 py-3 text-sm text-ct-text"
                role="status"
              >
                Er loopt een betalingsgeschil bij de kaartuitgever. Je toegang blijft voorlopig actief tot er een eindbeslissing
                is.
              </div>
            )}
            {(p.billing_status ?? 'active') === 'partially_refunded' && (
              <div
                className="mt-4 rounded-xl border border-ct-border bg-ct-elevated/50 px-4 py-3 text-sm text-ct-muted"
                role="status"
              >
                Er is een deel van het bedrag terugbetaald. Je digitale product blijft beschikbaar tenzij de rest ook wordt
                teruggeboekt.
              </div>
            )}
            {p.bundle_tier === 'plus' && (
              <label className="mt-4 block text-sm text-ct-muted">
                <span className="font-medium text-ct-text">Optionele naamregel (Plus)</span>
                <input
                  type="text"
                  value={brandingLine}
                  onChange={(e) => setBrandingLine(e.target.value)}
                  maxLength={120}
                  className="mt-1 w-full max-w-md rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-sm text-ct-text"
                  placeholder="Bijv. Namens Installatiebedrijf X"
                />
              </label>
            )}
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                disabled={
                  reportBusy ||
                  p.report_regenerations_used >= p.max_regenerations ||
                  purchaseBillingRevoked(p.billing_status ?? 'active')
                }
                onClick={() => void genReport()}
                className="rounded-xl bg-ct-accent px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {reportBusy
                  ? 'Rapport maken…'
                  : p.last_report_markdown
                    ? 'Rapport opnieuw genereren'
                    : 'Rapport genereren'}
              </button>
              <button
                type="button"
                disabled={csvBusy || purchaseBillingRevoked(p.billing_status ?? 'active')}
                onClick={() => void saveCsv()}
                className="rounded-xl border border-ct-border bg-ct-surface px-4 py-2.5 text-sm font-semibold text-ct-text hover:bg-ct-elevated disabled:opacity-50"
              >
                {csvBusy ? 'Bezig…' : 'Download overzicht (tabel)'}
              </button>
            </div>
            {p.last_report_markdown && (
              <div className="mt-8 max-h-[70vh] overflow-y-auto rounded-xl border border-ct-border bg-ct-bg p-5">
                <SimpleMarkdown text={p.last_report_markdown} />
                {p.last_report_disclaimer && (
                  <p className="mt-4 border-t border-ct-border pt-4 text-xs text-ct-muted">{p.last_report_disclaimer}</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
